/**
 * LandingView
 * 
 * Public landing page for Plot Planet.
 * Shows hero, features, and pricing preview.
 * Required for Stripe verification.
 */

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { GlassPanel, Button, Icon } from '../components/common'
import { PublicFooter } from '../components/common/PublicFooter'
import { APP_LOGO_TEXT, APP_TAGLINE, BRANDING } from '../config/branding'
import './LandingView.css'

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
}

// Feature data
const features = [
  {
    icon: 'characters',
    title: 'Characters & Worlds',
    description: 'Build rich character profiles, locations, and interconnected story elements with visual cards and relationships.'
  },
  {
    icon: 'timeline',
    title: 'Visual Timelines',
    description: 'Plot your story on an interactive timeline. See events, arcs, and how characters move through your narrative.'
  },
  {
    icon: 'images',
    title: 'Image Management',
    description: 'Organize reference images, concept art, and visual assets. Link images to characters, locations, and scenes.'
  },
  {
    icon: 'synopsis',
    title: 'Synopsis & Writing',
    description: 'Write and organize your story synopsis with a focused markdown editor. Layer your narrative beats.'
  },
  {
    icon: 'storyboard',
    title: 'Storyboarding',
    description: 'Plan visual sequences with drag-and-drop shot ordering. Perfect for screenwriters and visual storytellers.'
  },
  {
    icon: 'members',
    title: 'Team Collaboration',
    description: 'Invite collaborators to your projects. Assign roles and work together on your creative universe.'
  }
]

// Pricing tiers
const pricingTiers = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for getting started',
    features: [
      '1 Project',
      'Core story tools',
      'Basic image uploads',
      'Personal use'
    ],
    cta: 'Get Started',
    ctaVariant: 'outline'
  },
  {
    name: 'Pro',
    price: '$9',
    period: '/month',
    description: 'For serious storytellers',
    features: [
      'Unlimited Projects',
      'Team collaboration',
      'Priority support',
      'Advanced exports'
    ],
    cta: 'Start Free Trial',
    ctaVariant: 'primary',
    highlighted: true
  },
  {
    name: 'Lifetime',
    price: '$149',
    period: 'one-time',
    description: 'Own it forever',
    features: [
      'Everything in Pro',
      'Lifetime access',
      'No recurring fees',
      'Early access to features'
    ],
    cta: 'Get Lifetime',
    ctaVariant: 'secondary'
  }
]

export function LandingView() {
  return (
    <div className="landing-view">
      {/* Background effects */}
      <div className="landing-background">
        <div className="landing-grid" />
        <div className="landing-glow landing-glow-1" />
        <div className="landing-glow landing-glow-2" />
        <div className="landing-glow landing-glow-3" />
      </div>

      {/* Header */}
      <header className="landing-header">
        <Link to="/sandbox" className="landing-header__logo">
          <Icon name="logo-saturn" size={28} />
          <span>{APP_LOGO_TEXT}</span>
        </Link>
        <nav className="landing-header__nav">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/sandbox" className="landing-header__try">Try It Free</Link>
          <Link to="/login" className="landing-header__login">Sign in</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <motion.section 
        className="landing-hero"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <motion.h1 
          className="landing-hero__title"
          variants={fadeInUp}
        >
          {APP_LOGO_TEXT}
        </motion.h1>
        <motion.p 
          className="landing-hero__tagline"
          variants={fadeInUp}
        >
          {APP_TAGLINE}
        </motion.p>
        <motion.p 
          className="landing-hero__description"
          variants={fadeInUp}
        >
          A story development platform for writers, screenwriters, and world-builders.
          <br />
          Create characters, map timelines, and bring your narrative universe to life.
        </motion.p>
        <motion.div 
          className="landing-hero__cta"
          variants={fadeInUp}
        >
          <Link to="/sandbox">
            <Button variant="primary" size="lg">
              Try It Free
            </Button>
          </Link>
          <a href="#features">
            <Button variant="outline" size="lg">
              Learn More
            </Button>
          </a>
        </motion.div>
      </motion.section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="landing-section__container">
          <h2 className="landing-section__title">
            Everything you need to build your story universe
          </h2>
          <p className="landing-section__subtitle">
            Professional tools for creative storytelling, from concept to completion.
          </p>
          
          <div className="landing-features__grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <GlassPanel 
                  className="landing-feature-card"
                  hoverable
                  padding="lg"
                >
                  <div className="landing-feature-card__icon">
                    <Icon name={feature.icon} size={24} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </GlassPanel>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-pricing">
        <div className="landing-section__container">
          <h2 className="landing-section__title">
            Simple, transparent pricing
          </h2>
          <p className="landing-section__subtitle">
            Start free, upgrade when you're ready. No hidden fees.
          </p>
          
          <div className="landing-pricing__grid">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <GlassPanel 
                  className={`landing-pricing-card ${tier.highlighted ? 'landing-pricing-card--highlighted' : ''}`}
                  glow={tier.highlighted ? 'cyan' : undefined}
                  padding="lg"
                >
                  <div className="landing-pricing-card__header">
                    <h3>{tier.name}</h3>
                    <div className="landing-pricing-card__price">
                      <span className="price">{tier.price}</span>
                      <span className="period">{tier.period}</span>
                    </div>
                    <p className="landing-pricing-card__description">{tier.description}</p>
                  </div>
                  
                  <ul className="landing-pricing-card__features">
                    {tier.features.map((feature) => (
                      <li key={feature}>
                        <Icon name="check" size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/sandbox">
                    <Button 
                      variant={tier.ctaVariant} 
                      fullWidth
                    >
                      {tier.cta}
                    </Button>
                  </Link>
                </GlassPanel>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-section__container">
          <GlassPanel glow="cyan" padding="xl" className="landing-cta__panel">
            <h2>Ready to build your story universe?</h2>
            <p>Join writers and creators using {BRANDING.name} to bring their narratives to life.</p>
            <Link to="/sandbox">
              <Button variant="primary" size="lg">
                Try It Free — No Account Needed
              </Button>
            </Link>
          </GlassPanel>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  )
}

export default LandingView
